import React, { useEffect, useRef, useState } from "react";
import { Modal, ModalHeader, ModalBody, ModalFooter } from "reactstrap";
import { Button } from "components/ui";
import styles from "./dialog.module.scss";
import UploadIcon from "Assets/icon/icon-upload.svg";
import LoadingAnim from "Assets/anim/loading.gif";
import CheckIcon from "Assets/icon/icon-check.svg";
import { useUploadCSVMutation } from "apps/company/api/redux/api";
import { Link } from "react-router-dom";
import { generateCSV } from "apps/company/common/utils";
import { CommonModalProps } from "./common";
import { useAuth } from "providers/auth";
import { CompanyAdmin } from "apps/user/learner/api/types";
import Input from "./Input";
import { logError } from "utility/Function";
import { Trans } from "react-i18next";
import { useTranslation } from "lib/i18n/use-translation";

interface BulkUploadDialogProps extends CommonModalProps {
  exampleCSVURL?: string;
}

const downloadCSV = (csv: string) => {
  var link = document.createElement("a");
  link.href = "data:text/csv;charset=utf-8," + encodeURI(csv);
  link.download = "error_report.csv";
  document.body.appendChild(link);
  link.click();
};
const BulkUploadDialog = ({
  isOpen,
  exampleCSVURL,
  onCloseAction,
}: BulkUploadDialogProps) => {
  const handleNavigateCleanUp = () => {
    reset();
    onCloseAction();
  };
  //HOOKS
  const { companyAdmin } = useAuth();
  const { t } = useTranslation(["admin"]);

  const integrationsLink = [
    <Link to="/client/integrations" onClick={handleNavigateCleanUp} />,
  ];

  const defaultDescription = (
    <Trans
      i18nKey="admin:bulkUpload.description"
      components={integrationsLink}
    />
  );

  const successDescription = t("admin:bulkUpload.successDescription");

  const failedDescription = (
    <Trans
      i18nKey="admin:bulkUpload.failedDescription"
      components={integrationsLink}
    />
  );

  const failedDescriptionDuplicates = (
    <Trans
      i18nKey="admin:bulkUpload.failedDescriptionDuplicates"
      components={integrationsLink}
    />
  );

  const [description, setDescription] = useState<string | React.ReactNode>(
    defaultDescription
  );
  const [file, setFile] = useState<File | null>(null);
  const [isUploading, setIsUploading] = useState<boolean>(false);
  const [icon, setIcon] = useState<string>(UploadIcon);
  const [uploadCSV, { isLoading, isError }] = useUploadCSVMutation();
  const [erroText, setErrorText] = useState<string | React.ReactNode>("");
  const formRef = useRef(null);

  useEffect(() => {
    setIsUploading(isLoading);
    if (isError) {
      setDescription(failedDescription);
      setErrorText(t("admin:bulkUpload.uploadError"));
      setIcon(UploadIcon);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isLoading, isError]);

  //HANDLERS
  const handleFileSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    reset();
    setFile(e.target.files[0]);
  };

  const getCSVSample = () => {
    fetch(exampleCSVURL)
      .then((res) => res.blob())
      .then((result) => {
        var anchorTag = document.createElement("a");
        anchorTag.href = window.URL.createObjectURL(result);
        anchorTag.download = "examples_csv";
        anchorTag.click();
      })
      .catch((error) => {
        setErrorText(error.message);
        logError("Error downloading sample CSV", error);
      });
  };
  const reset = () => {
    setDescription(defaultDescription);
    setFile(null);
    setErrorText("");
    setIsUploading(false);
    setIcon(UploadIcon);
  };
  const toggle = () => {
    reset();
    onCloseAction();
  };

  const handleUpload = () => {
    if (file) {
      setIcon(LoadingAnim);

      let formData = new FormData();
      //Preparing for upload
      formData.append("members_csv", file);

      uploadCSV({
        id: (companyAdmin as CompanyAdmin).companyId,
        body: formData,
      })
        .then((result) => {
          //@ts-ignore
          if (result.data) {
            //@ts-ignore
            if (result.data.status === 200) {
              setIcon(CheckIcon);
              setDescription(successDescription);
              setErrorText("");

              /* @ts-ignore */
              if (!_.isEmpty(result.data.failed_user_list)) {
                /* @ts-ignore */
                const csv = generateCSV(result.data.failed_user_list);

                setDescription(failedDescriptionDuplicates);
                setErrorText(
                  <Trans
                    i18nKey="admin:bulkUpload.errorWithReport"
                    /* @ts-ignore */
                    values={{ message: result.data.data }}
                    components={[
                      <u
                        onClick={() => downloadCSV(csv)}
                        className={styles["link"]}
                      />,
                    ]}
                  />
                );
              }

              //@ts-ignore
            } else if (result.data.status === 400) {
              setDescription(failedDescription);
              setErrorText(
                <>
                  {/* @ts-ignore */}
                  {result.data.data}
                  <br />
                </>
              );
              setIcon(UploadIcon);
            } else {
              setDescription(failedDescription);
              //@ts-ignore
              setErrorText(result.data.data);
              setIcon(UploadIcon);
            }
          }
        })
        .finally(() => {
          formRef.current.reset();
        });
    } else {
      setErrorText(t("admin:bulkUpload.noFileSelected"));
      setIcon(UploadIcon);
    }
  };

  return (
    <Modal
      isOpen={isOpen}
      style={{ width: "100%", maxWidth: "440px" }}
      className={styles["modal"]}
      contentClassName={styles["modal-content"]}
      centered
    >
      <ModalHeader className={styles["header"]} toggle={toggle}></ModalHeader>
      <ModalBody className={styles["body"]}>
        <p className={styles["title"]}>{t("admin:bulkUpload.title")}</p>
        <p className={`text-small ${styles["bulk"]}`}>{description}</p>
        <u
          className={`text-small ${styles["bulk--link"]}`}
          onClick={getCSVSample}
        >
          {t("admin:bulkUpload.downloadSample")}
        </u>
        <br />
        <br />
        <div className={`sans ${styles.bulk}`}>
          <form ref={formRef}>
            <Input
              label={t("admin:bulkUpload.chooseFileLabel")}
              type="file"
              value={file?.name}
              placeholder={t("admin:bulkUpload.filePlaceholder")}
              onChange={handleFileSelect}
              readOnly
              icon2={icon}
            />
          </form>
        </div>
        <p className={`sans ${styles["error"]}`}>{erroText}</p>
      </ModalBody>
      <ModalFooter className={styles["footer"]}>
        <Button
          variant="primary"
          disabled={isLoading}
          onClick={() => !isUploading && handleUpload()}
          className="sans"
        >
          {t("admin:action.upload")}
        </Button>
      </ModalFooter>
    </Modal>
  );
};

export default BulkUploadDialog;
